import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  Mail, 
  Send, 
  Users, 
  Calendar,
  Eye,
  Settings as SettingsIcon,
  CheckCircle,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterManager() {
  const [contacts, setContacts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [contactsRes, previewRes, settingsRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/newsletter/contacts`, { headers }),
        axios.get(`${API_URL}/newsletter/preview`, { headers }),
        axios.get(`${API_URL}/newsletter/settings`, { headers }),
        axios.get(`${API_URL}/newsletter/logs?limit=10`, { headers })
      ]);
      
      setContacts(contactsRes.data.contacts);
      setPreview(previewRes.data);
      setSettings(settingsRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching newsletter data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const sendNewsletter = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/newsletter/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Newsletter inviata a ${response.data.recipients_count} contatti!`);
      fetchData(); // Refresh logs
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSending(false);
    }
  };

  const updateFrequency = async (frequency) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/newsletter/settings?frequency=${frequency}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Frequenza aggiornata!');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const previewNewsletter = () => {
    if (!preview) return;
    
    const html = generateNewsletterHTML();
    const newWindow = window.open();
    newWindow.document.write(html);
    newWindow.document.close();
  };

  const generateNewsletterHTML = () => {
    if (!preview) return '';
    
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Newsletter ZonePass</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f3f4f6;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f3f4f6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden;">
          
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 32px;">🎉 Eventi del Mese</h1>
              <p style="color: #e0e7ff; margin: 10px 0 0 0;">Scopri gli eventi più interessanti su ZonePass</p>
            </td>
          </tr>
          
          <!-- Events List -->
          <tr>
            <td style="padding: 40px 30px;">
              ${preview.events.slice(0, 10).map(event => {
                const startDate = new Date(event.start_date);
                return `
                <div style="margin-bottom: 30px; border-left: 4px solid #667eea; padding-left: 20px;">
                  <h3 style="color: #1f2937; margin: 0 0 10px 0; font-size: 20px;">${event.title}</h3>
                  <p style="color: #6b7280; margin: 5px 0; font-size: 14px;">
                    📅 ${startDate.toLocaleDateString('it-IT', {
                      weekday: 'long',
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                  <p style="color: #6b7280; margin: 5px 0; font-size: 14px;">
                    📍 ${event.location}
                  </p>
                  ${event.description ? `
                    <p style="color: #4b5563; margin: 10px 0; font-size: 14px; line-height: 1.5;">
                      ${event.description.substring(0, 150)}${event.description.length > 150 ? '...' : ''}
                    </p>
                  ` : ''}
                  <a href="https://yourdomain.com/events/${event.id}/public" 
                     style="display: inline-block; margin-top: 10px; background-color: #667eea; color: #ffffff; 
                            text-decoration: none; padding: 10px 20px; border-radius: 6px; font-size: 14px; font-weight: bold;">
                    🎟️ Prenota Ora
                  </a>
                </div>
                `;
              }).join('')}
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="background-color: #f9fafb; padding: 30px; text-align: center; border-top: 1px solid #e5e7eb;">
              <p style="color: #6b7280; font-size: 14px; margin: 0;">
                Ricevi questa email perché hai partecipato a un evento su ZonePass
              </p>
              <p style="color: #9ca3af; font-size: 12px; margin: 10px 0 0 0;">
                © ${new Date().getFullYear()} ZonePass - Piattaforma di Gestione Eventi
              </p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `;
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 text-center">
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              📧 Newsletter Automatica
            </h1>
            <p className="text-xl text-gray-600">
              Sistema di email marketing per eventi pubblici con frequenza adattiva
            </p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Contatti Database</p>
                    <p className="text-3xl font-bold text-gray-900">{contacts.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Eventi Prossimi 30gg</p>
                    <p className="text-3xl font-bold text-gray-900">{preview?.events_count || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Frequenza Attuale</p>
                    <p className="text-2xl font-bold text-gray-900 capitalize">
                      {settings?.frequency === 'monthly' ? 'Mensile' : 
                       settings?.frequency === 'biweekly' ? 'Quindicinale' :
                       settings?.frequency === 'weekly' ? 'Settimanale' : 'Automatica'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Frequency Settings */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon className="w-5 h-5" />
                Frequenza Newsletter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-900 font-semibold mb-2">🤖 Come Funziona:</p>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li><strong>Mensile:</strong> Inviata il 1° di ogni mese</li>
                  <li><strong>Quindicinale:</strong> Inviata il 1° e 15 di ogni mese</li>
                  <li><strong>Settimanale:</strong> Inviata ogni Lunedì</li>
                  <li><strong>Automatica:</strong> Sistema sceglie in base a numero eventi (10+ → quindicinale, 30+ → settimanale)</li>
                </ul>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button
                  onClick={() => updateFrequency('monthly')}
                  variant={settings?.frequency === 'monthly' ? 'default' : 'outline'}
                  className={settings?.frequency === 'monthly' ? 'bg-purple-600' : ''}
                >
                  📅 Mensile
                </Button>
                <Button
                  onClick={() => updateFrequency('biweekly')}
                  variant={settings?.frequency === 'biweekly' ? 'default' : 'outline'}
                  className={settings?.frequency === 'biweekly' ? 'bg-purple-600' : ''}
                >
                  📆 Quindicinale
                </Button>
                <Button
                  onClick={() => updateFrequency('weekly')}
                  variant={settings?.frequency === 'weekly' ? 'default' : 'outline'}
                  className={settings?.frequency === 'weekly' ? 'bg-purple-600' : ''}
                >
                  🗓️ Settimanale
                </Button>
                <Button
                  onClick={() => updateFrequency('auto')}
                  variant={settings?.frequency === 'auto' ? 'default' : 'outline'}
                  className={settings?.frequency === 'auto' ? 'bg-purple-600' : ''}
                >
                  🤖 Auto
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Send Newsletter */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Invia Newsletter Ora
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Button
                  onClick={previewNewsletter}
                  variant="outline"
                  className="flex-1 gap-2"
                  disabled={!preview || preview.events_count === 0}
                >
                  <Eye className="w-4 h-4" />
                  Anteprima Email
                </Button>

                <Button
                  onClick={sendNewsletter}
                  disabled={sending || !preview || preview.events_count === 0 || contacts.length === 0}
                  className="flex-1 bg-green-600 hover:bg-green-700 gap-2"
                >
                  {sending ? (
                    'Invio...'
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Invia a {contacts.length} Contatti
                    </>
                  )}
                </Button>
              </div>

              {(!preview || preview.events_count === 0) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Nessun evento pubblico nei prossimi 30 giorni. La newsletter non può essere inviata.
                  </p>
                </div>
              )}

              {contacts.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Nessun contatto nel database. Serve almeno un biglietto venduto per raccogliere email.
                  </p>
                </div>
              )}

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>🤖 Sistema Automatico Attivo:</strong> La newsletter viene inviata automaticamente 
                  secondo la frequenza impostata. Questo pulsante permette invio manuale immediato.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* History */}
          <Card>
            <CardHeader>
              <CardTitle>📜 Storico Invii</CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-gray-600 text-center py-8">Nessuna newsletter inviata ancora</p>
              ) : (
                <div className="space-y-3">
                  {logs.map((log, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-semibold text-gray-900">{log.subject}</p>
                          <p className="text-sm text-gray-600">
                            {new Date(log.sent_at).toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{log.recipients_count} destinatari</p>
                        <p className="text-sm text-gray-600">{log.events_count} eventi</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
